// polish.mjs — AI 润色的纯逻辑（SB-042 约定：可判定的部分入 .mjs 配单测）。
//
// 这里只有三件事，每一件都是 SB-067 验收里点名的：
//   ① **能不能按**（防呆）：太短的东西润色只会让模型编造内容。阈值与后端同值——
//      界面比接口窄或宽都出过事（DP-2），所以两处都有、且这里写明它是副本。
//   ② **一键还原**：润色是"直接替换草稿供用户评判"，那么**原稿必须留着**，
//      否则用户按一下就失去了自己写的东西，而他可能只是想看看。
//   ③ **剩余次数**：显示口径要与后端一致（-1 = 不限）。

// 与后端 defaults::kPolishMinChars 同值。**这是副本**：后端改了这里也要改，
// 两处不一致的方向都出过事——界面窄于接口时用户不会去试，宽于接口时按下去才报错。
import { polishLen, polishTrim } from './polish-len.mjs';

export const POLISH_MIN_CHARS = 12;

// 每个场景的长度上限（与后端 polish_scene_of 的表同值，同上属副本）。
export const POLISH_SCENES = {
  ticket_title: { maxChars: 200 },
  ticket_body: { maxChars: 20000 },
  reply: { maxChars: 20000 },
};

// 按钮该不该可按。返回 { enabled, reason } —— reason 是 i18n key 或 null。
// **不返回散文**：话术归 i18n，逻辑归这里（第 43 批语言规则）。
export function polishability(text, scene, quota) {
    const spec = POLISH_SCENES[scene];
    if (!spec) return { enabled: false, reason: 'polish.err.scene' };
    // 第一百一十六批（SB-143 ①）：长度改走 polish-len.mjs 这份**参考实现**，
    // 后端逐字对齐它（向量在 tests/fixtures/polish_len_vectors.h，cpp 侧逐条比对）。
    // 此前这里是 `t.length`（UTF-16 码元）而后端是字节，两个常量都是 12——
    // 于是一个 emoji 算两个、4~11 个汉字这里灰着而接口收。
    const t = polishTrim(text);
    const n = polishLen(text);
    if (n === 0) return { enabled: false, reason: null };   // 空框不提示，只是灰着
    if (n < POLISH_MIN_CHARS) return { enabled: false, reason: 'polish.err.tooShort' };
    if (n > spec.maxChars) return { enabled: false, reason: 'polish.err.tooLong' };
    // quota 尚未取到（null）时**允许按**：让一次请求去问后端，而不是把按钮锁死在
    // "我还不知道"上——后端会用 429 告诉我们，那是权威答案。
    if (quota && quota.limit > 0 && quota.remaining <= 0) {
      return { enabled: false, reason: 'polish.err.exhausted' };
    }
    return { enabled: true, reason: null };
}

// 剩余次数的显示值。-1 / limit<=0 = 不限（后端口径）。
export function remainingText(quota) {
  if (!quota) return null;
  if (!(quota.limit > 0)) return null;          // 不限就不显示数字，免得像个限制
  return { remaining: Math.max(0, quota.remaining), limit: quota.limit };
}

// 润色状态机。刻意做成纯数据：组件只持有它、渲染它。
// `original` 一旦被记下就**不再被覆盖**——连按两次润色，还原键仍然回到最初那一稿。
// 这是本模块最容易写错的一处：把 original 写成"上一稿"会让还原变成撤销一步，
// 而用户按还原时想要的是**他自己写的那份**。
export const EMPTY_POLISH = Object.freeze({ original: null, busy: false, error: null });

export function polishStarted(state, currentText) {
  return {
    original: state?.original ?? String(currentText ?? ''),   // 只记第一次
    busy: true,
    error: null,
  };
}

export function polishSucceeded(state) {
  return { original: state?.original ?? null, busy: false, error: null };
}

export function polishFailed(state, error) {
  // 失败时**不保留** original：没有发生替换，就不该出现"还原"按钮——
  // 一个按了没反应的按钮比没有按钮更难懂。
  return { original: state?.original ?? null, busy: false, error: error ?? 'unknown' };
}

export function polishReverted(state) {
  return { original: null, busy: false, error: null };
}

// 还原键该不该出现：有原稿、且不在忙。
export const canRevert = (state) => Boolean(state?.original !== null && !state?.busy);

// 把后端错误映射成 i18n key。**机器可辨的 code 才走 i18n**，自由文本原样显示
// （第 43 批语言规则）——所以 422 的 lost 列表要原样呈现，那是数据不是话术。
export function polishErrorKey(err) {
  const code = err?.code || err?.error?.code;
  if (code === 'POLISH_ALTERED_IDENTIFIERS') return 'polish.err.altered';
  if (code === 'RATE_LIMITED') return 'polish.err.exhausted';
  if (code === 'LLM_UNAVAILABLE') return 'polish.err.unavailable';
  if (code === 'RULE_VIOLATION') return 'polish.err.rejected';
  return 'polish.err.generic';
}
